import { Preferences } from "@capacitor/preferences";
import { useQuery } from "@tanstack/react-query";

import { getMultiPlatformList } from "api/methods";
import { queryClient } from "api/queryClient";
import { Models } from "api/types";
import { isNativeMobileApp } from "utils/platform.helpers";

type UsePlatformDomainTokensResult = {
  platformDomainTokens?: Models<"GetMultiPlatformUserPlatformResult">[];
};

export const usePlatformDomainTokens = (): UsePlatformDomainTokensResult => {
  const { data } = useQuery({
    ...getMultiPlatformList.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  return { platformDomainTokens: data?.platformDomainTokens };
};

/**
 * The practices this patient holds a token for, outside React.
 *
 * The native app keeps a copy in Preferences so the practice picker still works offline; the same
 * copy serves a push tapped before the list has loaded, which is the normal case for a notification
 * that cold-starts the app.
 */
export const loadPlatformDomainTokens = async (): Promise<
  Models<"GetMultiPlatformUserPlatformResult">[]
> => {
  try {
    const { platformDomainTokens } = await queryClient.fetchQuery({
      ...getMultiPlatformList.keyed(),
    });
    return platformDomainTokens;
  } catch {
    if (!isNativeMobileApp()) {
      return [];
    }

    const { value } = await Preferences.get({ key: "platformDomainTokens" });
    return value ? (JSON.parse(value) as Models<"GetMultiPlatformUserPlatformResult">[]) : [];
  }
};
